import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function LicenseManagement() {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    licensedTo: "",
    email: "",
    expiryDate: "",
    licenseType: "Standard",
    maxUsers: ""
  });
  const [generating, setGenerating] = useState(false);

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchLicenses();
  }, []);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const response = await api.get("/License/all");
      setLicenses(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch licenses");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!formData.licensedTo || !formData.expiryDate) {
      setError("Company name and expiry date are required");
      return;
    }

    setGenerating(true);
    setError("");

    try {
      const response = await api.post("/License/generate", {
        licensedTo: formData.licensedTo,
        email: formData.email || null,
        expiryDate: new Date(formData.expiryDate).toISOString(),
        licenseType: formData.licenseType,
        maxUsers: formData.maxUsers ? parseInt(formData.maxUsers) : null
      });

      setGeneratedKey(response.data);
      setFormData({
        licensedTo: "",
        email: "",
        expiryDate: "",
        licenseType: "Standard",
        maxUsers: ""
      });
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate license");
    } finally {
      setGenerating(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this license?")) return;

    try {
      await api.put(`/License/${id}/deactivate`);
      fetchLicenses();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to deactivate license");
    }
  };

  const handleActivate = async (licenseKey) => {
    try {
      await axios.post("/api/License/activate", { licenseKey });
      fetchLicenses();
      alert("License activated successfully!");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to activate license");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("License key copied to clipboard!");
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Set default expiry date to 1 year from now
  useEffect(() => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    setFormData(prev => ({
      ...prev,
      expiryDate: oneYearFromNow.toISOString().split("T")[0]
    }));
  }, []);

  if (loading) {
    return <div style={styles.loading}>Loading licenses...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🔑 License Management</h1>
          <p style={styles.subtitle}>Generate and manage application licenses</p>
        </div>
        <div style={styles.headerButtons}>
          <button onClick={() => setShowGenerateModal(true)} style={styles.generateBtn}>
            ➕ Generate New License
          </button>
          <button onClick={() => navigate("/admin")} style={styles.backBtn}>
            ← Back to Admin
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Current Active License */}
      {licenses.filter(l => l.isActive).map(license => (
        <div key={license.id} style={styles.activeCard}>
          <div style={styles.activeBadge}>✅ ACTIVE LICENSE</div>
          <div style={styles.activeContent}>
            <div style={styles.activeMain}>
              <h3 style={styles.activeLicensedTo}>{license.licensedTo}</h3>
              <p style={styles.activeLicenseKey}>{license.licenseKey}</p>
            </div>
            <div style={styles.activeDetails}>
              <div style={styles.activeDetail}>
                <span style={styles.detailLabel}>Type</span>
                <span style={styles.detailValue}>{license.licenseType}</span>
              </div>
              <div style={styles.activeDetail}>
                <span style={styles.detailLabel}>Expires</span>
                <span style={styles.detailValue}>{formatDate(license.expiryDate)}</span>
              </div>
              <div style={styles.activeDetail}>
                <span style={styles.detailLabel}>Days Left</span>
                <span style={{
                  ...styles.detailValue,
                  color: license.daysRemaining <= 30 ? "#dc3545" : "#28a745"
                }}>
                  {license.daysRemaining} days
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* All Licenses Table */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>📋 All Licenses</h2>
        
        {licenses.length === 0 ? (
          <p style={styles.noData}>No licenses found. Generate a new license to get started.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>License Key</th>
                  <th style={styles.th}>Licensed To</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Issued</th>
                  <th style={styles.th}>Expires</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license, idx) => (
                  <tr key={license.id} style={idx % 2 === 0 ? styles.trEven : {}}>
                    <td style={styles.td}>
                      <code style={styles.licenseCode}>{license.licenseKey}</code>
                      <button
                        onClick={() => copyToClipboard(license.licenseKey)}
                        style={styles.copyBtn}
                        title="Copy to clipboard"
                      >
                        📋
                      </button>
                    </td>
                    <td style={styles.td}>
                      <strong>{license.licensedTo}</strong>
                      {license.email && <div style={styles.email}>{license.email}</div>}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.typeBadge,
                        backgroundColor: license.licenseType === "Enterprise" ? "#6f42c1" : 
                                        license.licenseType === "Trial" ? "#fd7e14" : "#17a2b8"
                      }}>
                        {license.licenseType}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(license.issuedDate)}</td>
                    <td style={styles.td}>
                      {formatDate(license.expiryDate)}
                      <div style={{
                        fontSize: "11px",
                        color: license.daysRemaining <= 30 ? "#dc3545" : "#666"
                      }}>
                        {license.isExpired ? "Expired" : `${license.daysRemaining} days left`}
                      </div>
                    </td>
                    <td style={styles.td}>
                      {license.isActive ? (
                        <span style={styles.statusActive}>Active</span>
                      ) : license.isExpired ? (
                        <span style={styles.statusExpired}>Expired</span>
                      ) : (
                        <span style={styles.statusInactive}>Inactive</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {license.isActive ? (
                        <button
                          onClick={() => handleDeactivate(license.id)}
                          style={styles.deactivateBtn}
                        >
                          Deactivate
                        </button>
                      ) : !license.isExpired ? (
                        <button
                          onClick={() => handleActivate(license.licenseKey)}
                          style={styles.activateBtn}
                        >
                          Activate
                        </button>
                      ) : (
                        <span style={styles.expiredText}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate License Modal */}
      {showGenerateModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>🔑 Generate New License</h2>
            
            <form onSubmit={handleGenerate}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Company/Organization Name *</label>
                <input
                  type="text"
                  value={formData.licensedTo}
                  onChange={(e) => setFormData({...formData, licensedTo: e.target.value})}
                  placeholder="Enter company name"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email (Optional)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="contact@company.com"
                  style={styles.input}
                />
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>License Type</label>
                  <select
                    value={formData.licenseType}
                    onChange={(e) => setFormData({...formData, licenseType: e.target.value})}
                    style={styles.input}
                  >
                    <option value="Trial">Trial (30 days)</option>
                    <option value="Standard">Standard</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Max Users (Optional)</label>
                  <input
                    type="number"
                    value={formData.maxUsers}
                    onChange={(e) => setFormData({...formData, maxUsers: e.target.value})}
                    placeholder="Unlimited"
                    style={styles.input}
                    min="1"
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Expiry Date *</label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({...formData, expiryDate: e.target.value})}
                  style={styles.input}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>

              {/* Quick expiry presets */}
              <div style={styles.presets}>
                <span style={styles.presetLabel}>Quick Set:</span>
                {[
                  { label: "30 Days", days: 30 },
                  { label: "6 Months", days: 180 },
                  { label: "1 Year", days: 365 },
                  { label: "2 Years", days: 730 }
                ].map(preset => (
                  <button
                    key={preset.days}
                    type="button"
                    style={styles.presetBtn}
                    onClick={() => {
                      const date = new Date();
                      date.setDate(date.getDate() + preset.days);
                      setFormData({...formData, expiryDate: date.toISOString().split("T")[0]});
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div style={styles.modalButtons}>
                <button type="submit" disabled={generating} style={styles.submitBtn}>
                  {generating ? "Generating..." : "🔑 Generate License"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowGenerateModal(false);
                    setGeneratedKey(null);
                  }}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generated Key Display Modal */}
      {generatedKey && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.successIcon}>✅</div>
            <h2 style={styles.modalTitle}>License Generated Successfully!</h2>
            
            <div style={styles.generatedKeyBox}>
              <label style={styles.label}>License Key</label>
              <div style={styles.keyDisplay}>
                <code style={styles.keyCode}>{generatedKey.licenseKey}</code>
                <button
                  onClick={() => copyToClipboard(generatedKey.licenseKey)}
                  style={styles.copyKeyBtn}
                >
                  📋 Copy
                </button>
              </div>
            </div>

            <div style={styles.generatedDetails}>
              <p><strong>Licensed To:</strong> {generatedKey.licensedTo}</p>
              <p><strong>Type:</strong> {generatedKey.licenseType}</p>
              <p><strong>Expires:</strong> {formatDate(generatedKey.expiryDate)}</p>
            </div>

            <div style={styles.warning}>
              ⚠️ Save this license key securely. It will not be shown again in full.
            </div>

            <button
              onClick={() => setGeneratedKey(null)}
              style={styles.doneBtn}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "25px",
    flexWrap: "wrap",
    gap: "15px"
  },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "5px"
  },
  subtitle: {
    color: "#666",
    fontSize: "14px"
  },
  headerButtons: {
    display: "flex",
    gap: "10px"
  },
  generateBtn: {
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  backBtn: {
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  loading: {
    textAlign: "center",
    padding: "50px",
    fontSize: "18px"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "20px"
  },
  activeCard: {
    backgroundColor: "#d4edda",
    border: "2px solid #28a745",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px"
  },
  activeBadge: {
    backgroundColor: "#28a745",
    color: "white",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    display: "inline-block",
    marginBottom: "15px"
  },
  activeContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "20px"
  },
  activeMain: {},
  activeLicensedTo: {
    fontSize: "20px",
    color: "#155724",
    marginBottom: "5px"
  },
  activeLicenseKey: {
    fontFamily: "monospace",
    fontSize: "16px",
    color: "#155724"
  },
  activeDetails: {
    display: "flex",
    gap: "30px"
  },
  activeDetail: {
    textAlign: "center"
  },
  detailLabel: {
    display: "block",
    fontSize: "11px",
    color: "#666",
    marginBottom: "3px"
  },
  detailValue: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#155724"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "25px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "20px"
  },
  noData: {
    textAlign: "center",
    color: "#666",
    padding: "30px"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px"
  },
  th: {
    backgroundColor: "#f8f9fa",
    padding: "12px 10px",
    textAlign: "left",
    fontWeight: "600",
    borderBottom: "2px solid #dee2e6"
  },
  td: {
    padding: "12px 10px",
    borderBottom: "1px solid #eee",
    verticalAlign: "top"
  },
  trEven: {
    backgroundColor: "#f9f9f9"
  },
  licenseCode: {
    backgroundColor: "#f0f0f0",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "monospace"
  },
  copyBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    marginLeft: "8px",
    fontSize: "14px"
  },
  email: {
    fontSize: "11px",
    color: "#666"
  },
  typeBadge: {
    padding: "3px 8px",
    borderRadius: "4px",
    color: "white",
    fontSize: "11px",
    fontWeight: "600"
  },
  statusActive: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600"
  },
  statusExpired: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600"
  },
  statusInactive: {
    backgroundColor: "#e2e3e5",
    color: "#383d41",
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600"
  },
  deactivateBtn: {
    padding: "5px 12px",
    fontSize: "11px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer"
  },
  activateBtn: {
    padding: "5px 12px",
    fontSize: "11px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer"
  },
  expiredText: {
    color: "#999"
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "500px",
    maxHeight: "90vh",
    overflowY: "auto"
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "20px",
    textAlign: "center"
  },
  formGroup: {
    marginBottom: "18px"
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "15px"
  },
  label: {
    display: "block",
    marginBottom: "6px",
    fontWeight: "600",
    fontSize: "13px",
    color: "#333"
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    boxSizing: "border-box"
  },
  presets: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "20px",
    flexWrap: "wrap"
  },
  presetLabel: {
    fontSize: "12px",
    color: "#666"
  },
  presetBtn: {
    padding: "5px 10px",
    fontSize: "11px",
    backgroundColor: "#e9ecef",
    border: "1px solid #ddd",
    borderRadius: "15px",
    cursor: "pointer"
  },
  modalButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "20px"
  },
  submitBtn: {
    flex: 1,
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  cancelBtn: {
    flex: 1,
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#f0f0f0",
    color: "#333",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  successIcon: {
    fontSize: "48px",
    textAlign: "center",
    marginBottom: "15px"
  },
  generatedKeyBox: {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "20px"
  },
  keyDisplay: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  keyCode: {
    flex: 1,
    fontSize: "20px",
    fontFamily: "monospace",
    letterSpacing: "2px",
    color: "#28a745"
  },
  copyKeyBtn: {
    padding: "8px 15px",
    fontSize: "13px",
    backgroundColor: "#1a73e8",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  generatedDetails: {
    fontSize: "14px",
    marginBottom: "20px"
  },
  warning: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "12px",
    borderRadius: "6px",
    fontSize: "13px",
    marginBottom: "20px"
  },
  doneBtn: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#1a73e8",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  }
};

